import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import Stripe from 'stripe'

import {
  Button,
  Card,
  Input,
  Separator,
  showConfirmationPrompt,
  showErrorToast,
  showSuccessToast,
} from '@bpinternal/ui-kit-next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, DollarSign } from 'lucide-react'
import { Icon, PageSection } from '~/elements'
import { IconInput } from '~/elements/IconInput'
import { Spinner } from '~/elements/Spinner/Spinner'
import { QuotaType } from '@bpinternal/const'
import {
  BillingReadonlyCallout,
  ProductIcon,
  showCancelSubscriptionReasonDialog,
} from '~/features/billing/componentsV2'
import { formatPhases, formatSubItems } from '~/features/billing/helpers'
import { BillingApiClient, type PromoCode } from '~/features/billing/services'
import {
  type BillingVersion,
  COMMUNITY_PLAN_PRODUCT_ID,
  Plan,
  PLUS_PLAN_PRODUCT_ID,
  ProductWithPrices,
  TEAM_PLAN_PRODUCT_ID,
  useBilling,
} from '~/features/usage/useBilling'
import { useChangeAiSpendQuota, useCustomerPortal, useMonthlyCheckout, useUsage } from '~/hooks'
import { useSearchPromoCode } from '~/hooks/billing/usePromoCodeSearch'
import { useSetPromoCode } from '~/hooks/billing/useSetPromoCode'
import { useUpcomingInvoices } from '~/hooks/usage/useUpcomingInvoices'
import { trackEvent } from '~/providers'
import { AnalyticEvent } from '~/providers/SegmentProvider'
import { upcomingInvoiceQueryOptions } from '~/queries/billing'
import { getQueryKey } from '~/services'
import { cn, nanoToBase } from '~/utils'
import { useAppStore } from '~/stores'
import { getPlanColor } from '~/utils/getPlanColor'
import { AsyncButton } from '~/elements/AsyncButton'

/**
 * Formats a date with ordinal suffix (1st, 2nd, 3rd, etc.)
 * @param timestamp - Unix timestamp in seconds
 * @returns Formatted date string like "September 1st"
 */
const formatDateWithOrdinal = (timestamp: number): string => {
  const date = new Date(timestamp * 1000)
  const day = date.getDate()
  const suffix =
    day === 1 || day === 21 || day === 31
      ? 'st'
      : day === 2 || day === 22
        ? 'nd'
        : day === 3 || day === 23
          ? 'rd'
          : 'th'
  return date.toLocaleDateString('en-US', { month: 'long' }) + ' ' + day + suffix
}

export const Route = createFileRoute('/workspaces/$workspaceId/billing/summary')({
  component: () => {
    return <Component />
  },
})

type BillingProps = ReturnType<typeof useBilling>

const isProductPlan = (productId: string) => productId === TEAM_PLAN_PRODUCT_ID || productId === PLUS_PLAN_PRODUCT_ID

function Component() {
  const { workspaceId } = Route.useParams()
  const billingProps = useBilling(workspaceId)
  const billingReadonly = billingProps.billingInfo?.billingReadonly
  //TODO: This spinner has been done the lazy way, it should be replaced with a skeleton loader
  return billingProps.isLoading ? (
    <div className="flex h-9 w-full items-center justify-center">
      <Spinner />
    </div>
  ) : (
    <div className="flex flex-col gap-8">
      {billingReadonly && <BillingReadonlyCallout />}
      <BillingSummarySection billingReadonly={billingReadonly} {...billingProps} />
      <SubscriptionSection
        hasActiveSubscription={billingProps.hasActiveSubscription}
        billingVersion={billingProps.billingVersion}
        subscriptions={billingProps.subscriptions}
        subscriptionSchedule={billingProps.subscriptionSchedule}
        products={billingProps.products ?? []}
      />
    </div>
  )
}

// Backend currently throws an error when trying to fetch the billing information and the customer does not have a subscription, so we need to handle this case in the frontend
const BillingSummarySection = ({
  hasActiveSubscription,
  plan,
  products,
  promoCode,
  billingReadonly,
  ...rest
}: BillingProps & { plan: Plan; billingReadonly?: boolean }) => {
  const { workspaceId } = Route.useParams()
  const { ai_spend } = useUsage({
    workspaceId,
    quotas: ['ai_spend'],
    period: new Date().toISOString().split('T')[0] ?? '',
  })
  const { data } = useQuery(upcomingInvoiceQueryOptions(workspaceId))
  const { mutateAsync: fetchInvoices } = useUpcomingInvoices()
  const { mutate: updateAiSpendQuota, isPending: isUpdatingAiSpendQuota } = useChangeAiSpendQuota()
  const { mutate: openCustomerPortal } = useCustomerPortal(workspaceId)
  const { mutate: checkout, isPending: isCheckingOut } = useMonthlyCheckout(workspaceId, {
    hasActiveSubscription,
    promoCode,
    ...rest,
  })

  const [newAiSpendQuota, setNewAiSpendQuota] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const queryClient = useQueryClient()
  const currentUser = useAppStore((s) => s.currentUser)

  const accessUntilTs =
    rest.subscriptionSchedule?.current_phase?.end_date ??
    rest.subscriptionSchedule?.phases?.[0]?.end_date ??
    rest.subscription?.current_period_end

  const isCancellationScheduled = Boolean(
    rest.subscription?.cancel_at_period_end || rest.subscriptionSchedule?.end_behavior === 'cancel'
  )

  function handleUpdateAiSpendQuota() {
    const baseEvent: AnalyticEvent = {
      type: 'update_ai_spend',
      workspaceId,
      newAiSpend: newAiSpendQuota ?? 0,
      prevAiSpend: nanoToBase(ai_spend.quota),
      plan,
      state: 'clicked',
    } as const
    trackEvent({ ...baseEvent, state: 'clicked' })
    if (hasActiveSubscription) {
      return showConfirmationPrompt(
        <p>
          Are you sure you want to update your AI Spend Limit to{' '}
          <span className="font-bold">${Number(newAiSpendQuota ?? 0)}</span>?
        </p>,
        {
          title: 'Update AI Spend Limit',
          confirmLabel: 'Update',
        }
      ).then(() => {
        trackEvent({ ...baseEvent, state: 'confirmed' })
        updateAiSpendQuota({
          workspaceId,
          monthlySpendingLimit: newAiSpendQuota ?? undefined,
          onSuccess: () => setNewAiSpendQuota(null),
        })
      })
    }

    trackEvent({ ...baseEvent, state: 'credit_card_required' })
    return showConfirmationPrompt('Please add a credit card to increase your AI Spend limit.', {
      confirmLabel: 'Subscribe',
      title: 'Credit card required',
    }).then(() => {
      trackEvent({ ...baseEvent, state: 'clicked_add_card' })
      checkout()
    })
  }

  async function handleCancelSubscription() {
    const cancelSubBaseEvent: AnalyticEvent = {
      type: 'cancel_subscription',
      workspaceId,
      plan,
      success: false,
      state: 'errored',
    } as const

    if (!rest.subscription) {
      showErrorToast('No subscription found')
      return
    }

    if (hasActiveSubscription) {
      const cancelSubscription = await showConfirmationPrompt(
        <p>You will lose access to your plan and add-ons at the end of the current billing period.</p>,
        {
          title: 'Cancel Subscription',
          confirmLabel: 'I understand',
        }
      )

      if (!cancelSubscription) {
        return
      }

      const cancelReason = await showCancelSubscriptionReasonDialog()
      if (!cancelReason) {
        return
      }

      try {
        await BillingApiClient.saveCancellationReason(workspaceId, cancelReason.reasons, cancelReason.comment)
      } catch (error) {
        showErrorToast('Failed to save cancellation reason.')
      }

      try {
        setLoading(true)

        const payload =
          rest.subscription?.items?.data?.map((item) => ({
            price: item.price.id,
            quantity: 0,
          })) ?? []

        await BillingApiClient.updateSubscription(workspaceId, {
          productQuantityConfig: payload,
          isUpgrading: false,
          subscriptionId: COMMUNITY_PLAN_PRODUCT_ID,
        })
        await fetchInvoices({ workspaceId })

        showSuccessToast('Your subscription has been successfully cancelled.')
        trackEvent({ ...cancelSubBaseEvent, state: 'success', success: true })
        queryClient.invalidateQueries({
          queryKey: getQueryKey('workspaces_/$workspaceId_/subscriptionSchedule', { workspaceId }),
        })
      } catch (error) {
        showErrorToast('Failed to cancel subscription. Please try again later.')
        trackEvent({ ...cancelSubBaseEvent, state: 'errored', success: false })
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <Card className="p-4 @container">
      <div className="grid grid-cols-1 gap-6 @lg:grid-cols-[1fr,auto,1fr,auto,1fr]">
        <div className="flex flex-col gap-3">
          <p className="text-sm">Your next payment</p>
          <p className="text-xl font-medium">${((data?.total ?? 0) / 100).toFixed(2)}</p>
          <p className="text-xs text-muted-foreground">
            This amount includes the current spend on metered{' '}
            <a
              href="https://help.openai.com/en/articles/4936856-what-are-tokens-and-how-to-count-them"
              target="_blank"
              rel="noreferrer"
              className="text-accent-9 hover:underline"
            >
              AI usage.
            </a>
          </p>
        </div>
        <Separator className="@lg:h-full @lg:w-px" />
        <div className="flex flex-col gap-3">
          <p className="text-sm">AI Spend Limit</p>
          <p className="text-xl font-medium">${nanoToBase(ai_spend.quota).toFixed(2)}</p>
          <div className="flex items-center gap-1">
            <IconInput
              className="grow"
              disabled={isUpdatingAiSpendQuota || isCheckingOut || !hasActiveSubscription}
              value={newAiSpendQuota ?? ''}
              onChange={(e) => setNewAiSpendQuota(e.target.value ? Number(e.target.value) : null)}
              type="number"
              placeholder="10.00"
              startIcon={DollarSign}
            />
            <AsyncButton
              size="sm"
              disabled={(!newAiSpendQuota && hasActiveSubscription) || billingReadonly}
              loading={isUpdatingAiSpendQuota || isCheckingOut}
              onClick={() => {
                void handleUpdateAiSpendQuota()
              }}
              className="grid place-content-center"
            >
              <span>{hasActiveSubscription ? 'Update' : 'Subscribe'}</span>
            </AsyncButton>
          </div>
          <p className="text-xs text-muted-foreground">
            Maximum monthly limit for bot actions that use AI.{' '}
            <a
              className="text-accent-9 hover:underline"
              target="_blank"
              href="http://botpress.com/pricing"
              rel="noreferrer"
            >
              Learn more
            </a>
          </p>
        </div>
        <Separator className="@lg:h-full @lg:w-px" />
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <p className="text-sm">Payment information</p>
            <Button
              variant="secondary"
              onClick={() => openCustomerPortal()}
              className="hover:cursor-pointer hover:underline"
            >
              View invoices and payment information
            </Button>
          </div>
          {hasActiveSubscription && (
            <PromoCodeCard
              products={products}
              promoCode={promoCode}
              workspaceId={workspaceId}
              isCheckingOut={isCheckingOut}
            />
          )}
          <div className="flex flex-col gap-3">
            <p className="text-sm">Need help with billing?</p>
            <p className="text-xs">
              <span>Email: </span>
              <a href="mailto: finance@botpress.com" className="text-accent-9 hover:underline">
                finance@botpress.com
              </a>
            </p>
          </div>

          {plan !== 'enterprise' && plan !== 'community' && hasActiveSubscription && !isCancellationScheduled && (
            <AsyncButton
              size="sm"
              loading={loading}
              disabled={loading}
              variant="outline"
              className="!border-red-600/50 bg-transparent text-red-600 hover:bg-red-50 hover:text-red-700 dark:!border-red-400/50 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300"
              onClick={handleCancelSubscription}
            >
              Cancel Subscription
            </AsyncButton>
          )}
          {accessUntilTs && (
            <p className="text-xs text-muted-foreground">
              {`${plan.charAt(0).toUpperCase() + plan.slice(1)} plan`} until {formatDateWithOrdinal(accessUntilTs)}
            </p>
          )}
        </div>
      </div>
    </Card>
  )
}

const PromoCodeCard = ({
  workspaceId,
  promoCode,
  isCheckingOut,
  products = [],
  billingReadonly,
}: {
  workspaceId: string
  promoCode: PromoCode | null
  isCheckingOut: boolean
  products?: ProductWithPrices[]
  billingReadonly?: boolean
}) => {
  const [newPromoCode, setNewPromoCode] = useState<string | null>(null)
  const { promoCodeResult, invalid } = useSearchPromoCode({
    workspaceId,
    query: newPromoCode ?? '',
  })
  const { mutate: updatePromoCode, isPending: isUpdatingPromoCode } = useSetPromoCode({ workspaceId })

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const computeDescription = (promoCode: PromoCode) => {
    const {
      coupon: { durationInMonths, percentOff, appliesTo },
    } = promoCode
    const months = durationInMonths === 1 ? 'month' : 'months'
    let description = `${Math.trunc(percentOff)}% off for ${durationInMonths} ${months}`

    if (appliesTo.length > 0) {
      const appliedProducts = products.filter((p) => appliesTo.includes(p.id))
      const productNames = appliedProducts.map((p) => p.name).join(', ')
      description += ` on ${productNames}`
    }

    return description
  }

  return (
    <div>
      {promoCode && (
        <div className="flex flex-col gap-1">
          <p className="text-sm">Current promotion</p>
          <p className="text-xs">{computeDescription(promoCode)}</p>
        </div>
      )}
      {!promoCode && (
        <div>
          <p className="text-sm">Add Promo Code</p>
          <div className="flex items-center gap-1">
            <Input
              className="grow"
              disabled={isUpdatingPromoCode || isCheckingOut}
              value={newPromoCode ?? ''}
              onChange={(e) => setNewPromoCode(e.target.value ?? null)}
              type="text"
            />
            <AsyncButton
              size="sm"
              disabled={!promoCodeResult || invalid || billingReadonly}
              loading={isUpdatingPromoCode || isCheckingOut}
              onClick={() => {
                if (!newPromoCode) {
                  return
                }

                updatePromoCode({ code: newPromoCode })
              }}
              className="grid place-content-center"
            >
              <span>Add</span>
            </AsyncButton>
          </div>
          <p className={cn({ 'text-destructive': invalid && newPromoCode }, 'mt-2 text-xs')}>
            {promoCodeResult
              ? computeDescription(promoCodeResult)
              : invalid && newPromoCode
                ? 'This code is invalid.'
                : null}
          </p>
        </div>
      )}
    </div>
  )
}

const SubscriptionItem = ({
  productId,
  quotaName,
  quantity,
  productName,
}: {
  productId: string
  quotaName?: QuotaType
  quantity: number
  productName: string
}) => {
  const iconName: QuotaType | 'team' | 'plus' = quotaName
    ? quotaName
    : isProductPlan(productId) && productId === TEAM_PLAN_PRODUCT_ID
      ? 'team'
      : 'plus'

  const iconColor = isProductPlan(productId) ? getPlanColor(iconName as Plan) : undefined

  return (
    <div className="grid w-fit grid-cols-[auto,1fr,auto] items-center gap-3">
      <Icon icon={(props) => <ProductIcon quota={iconName} {...props} />} variant="soft" size="2" color={iconColor} />
      <p className="min-w-[200px] truncate text-sm font-medium">{productName}</p>
      <p className="text-xs font-medium text-muted-foreground">x {quantity}</p>
    </div>
  )
}

const SubscriptionCard = ({
  subscription,
  products,
  subscriptionSchedule,
}: {
  subscription: Stripe.Subscription
  products: ProductWithPrices[]
  subscriptionSchedule?: Stripe.SubscriptionSchedule
}) => {
  const isMonthlySub = subscription.items.data.every((item) => item.plan.interval === 'month')

  const hasPhases = (subscriptionSchedule?.phases?.length ?? 0) > 0

  return (
    <PageSection className="@container" title={`${isMonthlySub ? 'Monthly' : 'Yearly'} subscription`}>
      <div>
        {isMonthlySub && hasPhases ? (
          <div className="flex flex-col gap-8">
            {formatPhases(subscriptionSchedule?.phases ?? [], products).map((phase) => (
              <div key={phase.start_date}>
                {phase.items.length > 0 && (
                  <div className="flex flex-col gap-4" key={phase.start_date}>
                    <p className="font-medium text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <span>{phase.start_date}</span>
                        <Icon icon={ArrowRight} size="2" className="[&>svg]:stroke-[1.5px]" />
                        <span>{phase.end_date}</span>
                      </div>
                    </p>
                    {phase.items.map((item) => (
                      <SubscriptionItem
                        key={item.productId}
                        productId={item.productId}
                        quotaName={item.quotaName}
                        quantity={item.quantity}
                        productName={item.name}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {formatSubItems(subscription.items.data, products).map((subItem) => (
              <SubscriptionItem
                key={subItem.productId}
                productId={subItem.productId}
                quotaName={subItem.quotaName}
                quantity={subItem.quantity}
                productName={subItem.name}
              />
            ))}
          </div>
        )}
      </div>
    </PageSection>
  )
}
const SubscriptionSection = ({
  billingVersion,
  hasActiveSubscription,
  subscriptions,
  products,
  subscriptionSchedule,
}: {
  billingVersion: BillingVersion
  hasActiveSubscription: boolean
  subscriptions?: Stripe.Subscription[]
  products: ProductWithPrices[]
  subscriptionSchedule?: Stripe.SubscriptionSchedule
}) => {
  const [subscriptionsWithProducts, setSubscriptionsWithProducts] = useState<Stripe.Subscription[]>([])
  //Filter out monthly susbcriptions that only have web search and ai tokens products
  useEffect(() => {
    setSubscriptionsWithProducts(
      subscriptions?.filter((sub) =>
        sub.items.data.every((item) => item.price.recurring?.interval === 'month') ? sub.items.data.length > 2 : true
      ) ?? []
    )
  }, [subscriptions, hasActiveSubscription])

  const isLegacyPricing = billingVersion === 'v1' || billingVersion === 'v2'

  return (
    <>
      {!subscriptionsWithProducts?.length && (
        <p className="p-4 text-center text-base">You don&apos;t have any active subscriptions</p>
      )}
      {subscriptionsWithProducts.map((sub) => (
        <SubscriptionCard
          key={sub.id}
          subscription={sub}
          products={products}
          subscriptionSchedule={subscriptionSchedule}
        />
      ))}
      {isLegacyPricing && (
        <p className="pl-2 text-sm font-medium text-muted-foreground">
          * This workspace is subject to legacy pricing and may not reflect prices listed on the Botpress website.
          Please refer to{' '}
          {
            <a
              className="text-accent-9 hover:underline"
              href="https://botpress.com/pricing#price-faq"
              target="_blank"
              rel="noreferrer"
            >
              this page
            </a>
          }{' '}
          for more information
        </p>
      )}
    </>
  )
}
